import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Send, ShieldCheck, CheckCircle2, AlertTriangle, Copy, Search,
  ChevronRight, ChevronLeft, CheckSquare, User, Users, Clock, FileSignature,
  Lock, Mail, Phone, Calendar, MessageSquare, Hash, Briefcase, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { RECRUITMENT_BG_URL } from "../components/LSSDLogo";
import { formatApiErrorDetail } from "../context/AuthContext";

const REQUIREMENTS = [
  "Cittadinanza Americana o permesso di lavoro valido",
  "Età minima di 21 anni",
  "Certificato FDO (DA FARE IN OSPEDALE)",
  "Nessun precedente penale grave",
  "Possesso di licenza di guida",
  "Diploma di scuola superiore o titolo equivalente",
  "Ottime capacità comunicative e di problem-solving",
  "Buona salute fisica e mentale",
  "Conoscenza della legge",
  "Capacità di lavorare in team e sotto pressione",
];

const STEPS = [
  { id: 1, label: "Requisiti", icon: CheckSquare },
  { id: 2, label: "Dati IC", icon: User },
  { id: 3, label: "Dati OOC", icon: Users },
  { id: 4, label: "Disponibilità", icon: Clock },
  { id: 5, label: "Dichiarazione", icon: FileSignature },
];

const initialForm = {
  // requisiti
  reqs: REQUIREMENTS.map(() => false),
  // IC
  rp_name: "", rp_dob: "", phone_rp: "", email_rp: "",
  // OOC
  discord_id: "", past_experience: "", nickname: "",
  // Disponibilità
  availability: "",
  // Dichiarazione
  no_record_confirmed: false,
};

export default function Recruitment() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [recruitment, setRecruitment] = useState({ open: true, message: null });
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    api.get("/settings/recruitment").then(({ data }) => setRecruitment(data)).catch(() => {}).finally(() => setLoadingStatus(false));
  }, []);

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "object" && v?.target ? v.target.value : v }));
  const toggleReq = (i) => setForm((f) => { const r = [...f.reqs]; r[i] = !r[i]; return { ...f, reqs: r }; });
  const allReqsChecked = form.reqs.every(Boolean);

  const canProceed = {
    1: allReqsChecked,
    2: form.rp_name.trim() && form.rp_dob && form.phone_rp.trim() && form.email_rp.trim(),
    3: form.discord_id.trim() && form.nickname.trim(),
    4: !!form.availability,
    5: form.no_record_confirmed,
  };

  const next = () => {
    if (!canProceed[step]) {
      toast.error("Completa tutti i campi obbligatori prima di proseguire");
      return;
    }
    setStep((s) => Math.min(5, s + 1));
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const triggerSubmit = () => {
    if (!canProceed[5]) {
      toast.error("Devi confermare la dichiarazione");
      return;
    }
    setShowConfirm(true);
  };

  const doSubmit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        rp_name: form.rp_name,
        rp_dob: form.rp_dob,
        phone_rp: form.phone_rp,
        email_rp: form.email_rp,
        discord_id: form.discord_id,
        nickname: form.nickname,
        past_experience: form.past_experience,
        availability: form.availability,
        no_record_confirmed: form.no_record_confirmed,
        requirements_acknowledged: allReqsChecked,
      };
      const { data } = await api.post("/candidatures", payload);
      setSuccess({ tracking_code: data.tracking_code });
      setShowConfirm(false);
      setForm(initialForm);
      setStep(1);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore durante l'invio");
      setShowConfirm(false);
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).catch(() => {});
    toast.success("Codice copiato!");
  };

  // BANDI CHIUSI
  if (!loadingStatus && !recruitment.open) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="recruitment-closed">
        <div className="bg-[#143830] border-2 border-rose-500/40 rounded-2xl p-10">
          <Lock className="h-16 w-16 text-rose-400 mx-auto mb-5" />
          <h1 className="font-heading font-black text-3xl text-[#F5E6B8] mb-3">Bandi Chiusi</h1>
          <p className="text-[#D4C480] mb-4 leading-relaxed">
            I bandi di reclutamento del Los Santos County Sheriff Department sono momentaneamente chiusi.
          </p>
          {recruitment.message && (
            <div className="bg-sheriff-gold/10 border border-sheriff-gold/30 rounded-xl p-4 my-5 text-left">
              <div className="uppercase-label text-sheriff-gold mb-2">Comunicato del Comando</div>
              <p className="text-[#E8D89A] whitespace-pre-wrap">{recruitment.message}</p>
            </div>
          )}
          <p className="text-sm text-[#B8A269]">Torna a controllare nei prossimi giorni o segui i comunicati ufficiali sulla Home.</p>
          <Link to="/" className="inline-block mt-6">
            <Button className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold">
              Torna alla Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // SUCCESS SCREEN
  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="recruitment-success">
        <div className="bg-[#143830] border-2 border-sheriff-gold rounded-2xl p-10">
          <CheckCircle2 className="h-16 w-16 text-sheriff-gold mb-5" />
          <h1 className="font-heading font-black text-3xl text-[#F5E6B8] mb-3">Candidatura Ricevuta</h1>
          <p className="text-[#D4C480] mb-6 leading-relaxed">
            La tua domanda è stata registrata con stato <span className="font-mono-codes text-sheriff-gold font-bold">IN SOSPESO</span>.
            <br />Salva il codice qui sotto per controllare lo stato in qualsiasi momento.
          </p>
          <div className="bg-[#0A1F18] border border-sheriff-gold/40 rounded-xl p-5 mb-6">
            <div className="uppercase-label text-sheriff-gold mb-2">Codice di Tracciamento</div>
            <div className="flex items-center justify-between gap-3">
              <div className="font-mono font-black text-3xl text-sheriff-gold tracking-[0.2em]" data-testid="tracking-code">{success.tracking_code}</div>
              <button onClick={() => copyCode(success.tracking_code)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sheriff-gold/15 hover:bg-sheriff-gold/30 border border-sheriff-gold/40 text-sheriff-gold rounded-full text-xs uppercase tracking-[0.15em] font-bold transition-colors">
                <Copy className="h-3.5 w-3.5" /> Copia
              </button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to={`/candidatura/${success.tracking_code}`} className="flex-1">
              <Button className="w-full bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold">
                <Search className="h-4 w-4 mr-2" /> Vedi Stato
              </Button>
            </Link>
            <Button onClick={() => setSuccess(null)} variant="outline" className="flex-1 border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label">
              Nuova Candidatura
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 min-h-[calc(100vh-80px)]" data-testid="recruitment-page">
      <div className="bg-[#0F2620] px-6 sm:px-10 lg:px-14 py-12">
        <div className="max-w-xl mx-auto">
          {/* HEADER */}
          <div className="uppercase-label text-sheriff-gold mb-2">Modulo Bando</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-[#F5E6B8] mb-3">Candidatura<br />Vice Sceriffo</h1>
          <p className="text-[#D4C480] mb-8 text-sm leading-relaxed">Procedi step-by-step: ogni sezione si sblocca dopo aver completato la precedente.</p>

          {/* STEPPER */}
          <div className="flex items-center justify-between mb-10 gap-1 sm:gap-2">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = step > s.id;
              const active = step === s.id;
              return (
                <div key={s.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div className={`relative h-10 w-10 rounded-full flex items-center justify-center border-2 transition-all ${
                      done ? "bg-sheriff-gold border-sheriff-gold text-[#0A1F18]" :
                      active ? "bg-sheriff-gold/15 border-sheriff-gold text-sheriff-gold shadow-lg shadow-sheriff-gold/20 scale-110" :
                      "bg-[#0A1F18] border-sheriff-gold/30 text-[#8B7B49]"
                    }`}>
                      {done ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <div className={`text-[9px] uppercase tracking-[0.1em] font-bold mt-1.5 text-center ${
                      active ? "text-sheriff-gold" : done ? "text-[#D4C480]" : "text-[#8B7B49]"
                    }`}>{s.label}</div>
                  </div>
                  {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 -mt-5 ${done ? "bg-sheriff-gold" : "bg-sheriff-gold/20"}`} />}
                </div>
              );
            })}
          </div>

          {/* STEP CONTENT */}
          <div className="space-y-5" data-testid={`recruitment-step-${step}`}>
            {step === 1 && (
              <Step title="Requisiti d'Accesso" subtitle="Conferma di soddisfare tutti i requisiti elencati.">
                <div className="space-y-2">
                  {REQUIREMENTS.map((r, i) => (
                    <label key={r} className={`flex items-start gap-3 cursor-pointer p-3 border rounded-xl transition-colors ${
                      form.reqs[i] ? "bg-emerald-500/10 border-emerald-500/40" : "bg-[#0A1F18] border-sheriff-gold/20 hover:border-sheriff-gold/50"
                    }`}>
                      <Checkbox checked={form.reqs[i]} onCheckedChange={() => toggleReq(i)} data-testid={`req-${i}`} className="mt-0.5" />
                      <span className={`text-sm ${form.reqs[i] ? "text-[#F5E6B8]" : "text-[#D4C480]"}`}>{r}</span>
                    </label>
                  ))}
                </div>
                <button
                  onClick={() => setForm((f) => ({ ...f, reqs: REQUIREMENTS.map(() => true) }))}
                  className="text-xs uppercase tracking-[0.15em] font-bold text-sheriff-gold hover:text-sheriff-gold-dark transition-colors mt-3"
                  data-testid="req-check-all"
                >
                  ✓ Spunta Tutti
                </button>
              </Step>
            )}

            {step === 2 && (
              <Step title="Dati In Character" subtitle="Le informazioni del tuo personaggio nel server Roleplay.">
                <Field label="Nome e Cognome" icon={User}>
                  <Input data-testid="rec-rp-name" required value={form.rp_name} onChange={update("rp_name")} placeholder="John Marston" />
                </Field>
                <Field label="Data di Nascita" icon={Calendar}>
                  <Input data-testid="rec-rp-dob" type="date" required value={form.rp_dob} onChange={update("rp_dob")} />
                </Field>
                <Field label="Numero di Telefono" icon={Phone}>
                  <Input data-testid="rec-phone" required value={form.phone_rp} onChange={update("phone_rp")} placeholder="555-1234" />
                </Field>
                <Field label="E-Mail" icon={Mail}>
                  <Input data-testid="rec-email" type="email" required value={form.email_rp} onChange={update("email_rp")} placeholder="andrearossi@gmail.com" />
                </Field>
              </Step>
            )}

            {step === 3 && (
              <Step title="Dati Out Of Character" subtitle="Le informazioni del giocatore reale dietro al personaggio.">
                <Field label="ID Discord" icon={Hash}>
                  <Input data-testid="rec-discord" required value={form.discord_id} onChange={update("discord_id")} placeholder="user#0000 o 123456789012345678" />
                </Field>
                <Field label="Nickname" icon={MessageSquare}>
                  <Input data-testid="rec-nickname" required value={form.nickname} onChange={update("nickname")} placeholder="ShadowWolf" />
                </Field>
                <Field label="Esperienze Passate (opzionale)" icon={Briefcase}>
                  <Textarea
                    data-testid="rec-past-experience"
                    value={form.past_experience}
                    onChange={update("past_experience")}
                    placeholder="Descrivi brevemente le tue esperienze precedenti in altre community o ruoli simili…"
                    rows={4}
                  />
                </Field>
              </Step>
            )}

            {step === 4 && (
              <Step title="Disponibilità Oraria" subtitle="Indica quando preferisci sostenere il colloquio.">
                <Field label="Fascia oraria" icon={Clock}>
                  <Select value={form.availability} onValueChange={(v) => setForm((f) => ({ ...f, availability: v }))}>
                    <SelectTrigger data-testid="rec-availability-trigger" className="rounded-xl">
                      <SelectValue placeholder="Seleziona la fascia oraria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mattina (09:00 - 12:00)">Mattina (09:00 - 12:00)</SelectItem>
                      <SelectItem value="Pomeriggio (14:00 - 18:00)">Pomeriggio (14:00 - 18:00)</SelectItem>
                      <SelectItem value="Sera (19:00 - 22:00)">Sera (19:00 - 22:00)</SelectItem>
                      <SelectItem value="Notte (22:00 - 01:00)">Notte (22:00 - 01:00)</SelectItem>
                      <SelectItem value="Weekend">Weekend</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </Step>
            )}

            {step === 5 && (
              <Step title="Dichiarazione Ufficiale" subtitle="Conferma sotto la tua responsabilità.">
                <label className={`flex items-start gap-3 cursor-pointer p-4 border rounded-xl transition-colors ${
                  form.no_record_confirmed ? "bg-emerald-500/10 border-emerald-500/40" : "bg-[#0A1F18] border-sheriff-gold/30 hover:border-sheriff-gold/60"
                }`}>
                  <Checkbox data-testid="rec-no-record-checkbox" checked={form.no_record_confirmed} onCheckedChange={(v) => setForm((f) => ({ ...f, no_record_confirmed: !!v }))} className="mt-0.5" />
                  <div>
                    <div className="font-semibold text-[#F5E6B8] text-sm">Dichiaro sotto la mia responsabilità</div>
                    <div className="text-xs text-[#D4C480] mt-1 leading-relaxed">
                      di <span className="font-bold text-sheriff-gold">non avere precedenti penali gravi</span> all&apos;interno del server RP e che tutte le informazioni fornite in questa candidatura sono veritiere.
                    </div>
                  </div>
                </label>
                <div className="bg-sheriff-gold/8 border border-sheriff-gold/25 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <ShieldCheck className="h-5 w-5 text-sheriff-gold shrink-0 mt-0.5" />
                    <p className="text-xs text-[#D4C480] leading-relaxed">
                      Eventuali dichiarazioni false comportano l&apos;esclusione automatica dal processo di selezione e la segnalazione al Comando.
                    </p>
                  </div>
                </div>
              </Step>
            )}

            {error && (
              <div data-testid="recruitment-error" className="flex items-start gap-2 p-3 bg-rose-500/10 border border-rose-500/40 rounded-xl text-sm text-rose-300">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* NAVIGATION */}
          <div className="mt-8 flex items-center justify-between gap-3 pt-6 border-t border-sheriff-gold/15">
            <Button
              variant="outline"
              onClick={prev}
              disabled={step === 1}
              data-testid="step-prev"
              className="border-sheriff-gold/30 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Indietro
            </Button>
            <div className="text-xs text-[#8B7B49] font-mono">Step {step} di {STEPS.length}</div>
            {step < 5 ? (
              <Button onClick={next} data-testid="step-next" className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold">
                Avanti <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={triggerSubmit} disabled={!canProceed[5]} data-testid="rec-submit-btn" className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold">
                <Send className="h-4 w-4 mr-2" /> Invia Candidatura
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT - IMAGE */}
      <div
        className="hidden lg:block relative grain-overlay"
        style={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.30), rgba(27,77,62,0.60)), url(${RECRUITMENT_BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 flex items-end p-12">
          <div className="text-white">
            <ShieldCheck className="h-10 w-10 text-sheriff-gold mb-4" />
            <div className="uppercase-label text-sheriff-gold mb-2">Codice 10-8</div>
            <h2 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-tight max-w-md">
              Pronto al servizio.<br />Pronto a fare la differenza.
            </h2>
            <p className="font-mono-codes text-xs text-white/80 mt-4">A TRADITION OF SERVICE SINCE 1852</p>
          </div>
        </div>
      </div>

      {/* CONFIRM DIALOG */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/50 text-[#F5E6B8] rounded-2xl" data-testid="recruitment-confirm-dialog">
          <DialogHeader>
            <div className="mx-auto h-16 w-16 rounded-full bg-sheriff-gold/15 border-2 border-sheriff-gold/40 flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-sheriff-gold" />
            </div>
            <DialogTitle className="font-heading font-black text-2xl text-center text-[#F5E6B8] leading-tight">
              Sei sicuro di voler<br />compilare la candidatura?
            </DialogTitle>
          </DialogHeader>
          <p className="text-center text-[#D4C480] text-sm leading-relaxed px-4">
            Stai inviando ufficialmente la tua domanda al<br /><span className="text-sheriff-gold font-bold">Los Santos County Sheriff Department</span>.
            <br /><br />Una volta inviata, la candidatura sarà revisionata dall&apos;Ufficio Risorse Umane.
          </p>
          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirm(false)}
              disabled={submitting}
              className="flex-1 border-sheriff-gold/30 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label"
              data-testid="confirm-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={doSubmit}
              disabled={submitting}
              className="flex-1 bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold"
              data-testid="confirm-submit"
            >
              {submitting ? "Invio in corso…" : "Continua"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Step({ title, subtitle, children }) {
  return (
    <div>
      <div className="mb-5">
        <h2 className="font-heading font-bold text-2xl text-[#F5E6B8] mb-1">{title}</h2>
        <p className="text-sm text-[#B8A269]">{subtitle}</p>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-sheriff-gold mb-1.5 block flex items-center gap-1.5 uppercase tracking-[0.15em]">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </Label>
      {children}
    </div>
  );
}
